import { api, axiosInstance } from '@/lib/api';
import { resolveUploadsUrl } from '@/lib/resolve-uploads-url';

export interface UserImage {
  id: string;
  filename: string;
  url: string;
  fileSize: number | null;
  mimeType: string | null;
  category: string | null;
  title?: string | null;
  description?: string | null;
  createdAt: string;
  updatedAt?: string;
}

export type PatchUserImageBody = {
  title?: string | null;
  description?: string | null;
  filename?: string;
};

/** Libellé principal dans les listes / bibliothèque (titre saisi ou nom de fichier affiché). */
export function libraryImageDisplayName(img: Pick<UserImage, 'title' | 'filename'>): string {
  const t = img.title?.trim();
  return t && t.length > 0 ? t : img.filename;
}

export const imagesApi = {
  list: () => api.get<UserImage[]>('/images'),

  upload: (
    file: File,
    category = 'general',
    onProgress?: (p: number) => void,
    meta?: { title?: string; description?: string },
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    if (meta?.title?.trim()) formData.append('title', meta.title.trim());
    if (meta?.description?.trim()) formData.append('description', meta.description.trim());
    return api.upload('/images/upload', formData, onProgress) as Promise<{ data: UserImage }>;
  },

  update: async (id: string, body: PatchUserImageBody): Promise<UserImage> => {
    const r = await api.patch<{ success?: boolean; data?: UserImage }>(`/images/${id}`, body);
    if (r && typeof r === 'object' && 'data' in r && r.data) return r.data;
    return r as unknown as UserImage;
  },

  replace: async (id: string, file: File, onProgress?: (p: number) => void): Promise<UserImage> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await axiosInstance.post<{ success?: boolean; data?: UserImage }>(
      `/images/${id}/replace`,
      formData,
      {
        onUploadProgress: (event) => {
          if (onProgress && event.total) {
            onProgress(Math.round((event.loaded * 100) / event.total));
          }
        },
      },
    );
    const b = res.data;
    if (b && typeof b === 'object' && 'data' in b && b.data) return b.data;
    return b as unknown as UserImage;
  },

  delete: (id: string) => api.delete(`/images/${id}`),

  /** Resolve full URL for display (handles /uploads/... relative paths) */
  resolveUrl: (url: string) => resolveUploadsUrl(url) ?? '',

  /**
   * URL absolue pour partage / presse-papiers (origine du navigateur + chemin si l’URL est relative).
   */
  publicUrlForShare: (url: string): string => {
    const r = resolveUploadsUrl(url) ?? '';
    if (!r) return '';
    if (typeof window !== 'undefined' && r.startsWith('/')) {
      return `${window.location.origin}${r}`;
    }
    return r;
  },

  /** Évite le cache navigateur après remplacement (même chemin, nouveau contenu). */
  cacheBustUrl: (url: string, updatedAt?: string | null) => {
    const base = resolveUploadsUrl(url) ?? '';
    if (!base || !updatedAt) return base;
    const v = Date.parse(updatedAt);
    if (Number.isNaN(v)) return base;
    return `${base}${base.includes('?') ? '&' : '?'}v=${v}`;
  },
};
